import { createFileRoute, Link, notFound } from "@tanstack/react-router";
import { z } from "zod";
import { getClientPortal } from "@/lib/portal.functions";
import type { PortalProject } from "@/lib/portal-shared";
import { Layout } from "@/components/site/Layout";

export const Route = createFileRoute("/clients/$client/projects/")({
  validateSearch: z.object({ t: z.string().optional() }),
  loaderDeps: ({ search }) => ({ t: search.t ?? "" }),
  loader: async ({ params, deps }) => {
    if (!deps.t) throw notFound();
    const res = await getClientPortal({ data: { client: params.client, token: deps.t } });
    if (!res.ok || !res.client) throw notFound();
    return { client: res.client, projects: res.projects ?? [] };
  },
  head: () => ({
    meta: [
      { title: "Client workspace — PULSO33" },
      { name: "robots", content: "noindex, nofollow" },
      { name: "description", content: "Private client workspace with project proposals from PULSO33." },
    ],
  }),
  component: ClientProjectsPage,
});

function ClientProjectsPage() {
  const data = Route.useLoaderData();
  const { t } = Route.useSearch();
  const params = Route.useParams();

  return (
    <Layout>
      <section className="mx-auto w-full max-w-4xl px-4 py-16 md:py-24">
        <p className="font-mono text-xs uppercase tracking-[0.2em] text-primary">
          Private workspace
        </p>
        <h1 className="mt-3 font-display text-3xl md:text-4xl text-foreground">
          {data.client?.name}
        </h1>
        {data.client?.notes ? (
          <p className="mt-3 max-w-2xl text-sm text-muted-foreground">{data.client.notes}</p>
        ) : null}

        <div className="mt-10 grid gap-4">
          {data.projects.length === 0 ? (
            <p className="text-sm text-muted-foreground">No projects published yet.</p>
          ) : (
            data.projects.map((p: PortalProject) => (
              <Link
                key={p.id}
                to="/clients/$client/projects/$project"
                params={{ client: params.client, project: p.slug }}
                search={{ t }}
                className="group rounded-lg border border-border bg-card p-5 transition-colors hover:border-primary"
              >
                <h2 className="font-display text-xl text-foreground group-hover:text-primary">
                  {p.name}
                </h2>
                {p.description ? (
                  <p className="mt-2 text-sm text-muted-foreground">{p.description}</p>
                ) : null}
              </Link>
            ))
          )}
        </div>
      </section>
    </Layout>
  );
}
